import { useEffect, useRef, useState, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { LanguageProvider } from "./i18n/LanguageContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import NewDecision from "./pages/NewDecision";
import SimulationChat from "./pages/SimulationChat";
import SimulationResult from "./pages/SimulationResult";
import History from "./pages/History";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import ConsultaDirecta from "./pages/ConsultaDirecta";
import Terminos from "./pages/Terminos";
import Privacidad from "./pages/Privacidad";
import CookiePolicy from "./pages/CookiePolicy";
import DeleteAccountPublic from "./pages/DeleteAccountPublic";
import Comparador from "./pages/Comparador";
import MoodTracker from "./pages/MoodTracker";
import PremiumLibrary from "./pages/PremiumLibrary";
import ForgotPassword from "./pages/ForgotPassword";
import Profile from "./pages/Profile";
import CookieBanner from "./components/CookieBanner";
import DiaryAI from "./pages/DiaryAI";
import ConsultaDetail from "./pages/ConsultaDetail";
import AvatarWidget from "./components/AvatarWidget";
import { AvatarProvider } from "./hooks/useAvatarContext";
import TimelinePage from "./pages/TimelinePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Global Axios timeout: 15 seconds
axios.defaults.timeout = 15000;
axios.defaults.withCredentials = true;
// Note: browsers handle HTTP/1.1 keep-alive and HTTP/2 connection reuse automatically.
// Setting the "Connection" header via JS is forbidden by the Fetch spec (silently ignored + console warning).

// In-memory + storage token for cross-domain WebView compatibility.
// Priority: localStorage (persists across app restarts) -> sessionStorage (fallback).
let _bearerToken = null;
try { _bearerToken = localStorage.getItem("_ft") || sessionStorage.getItem("_ft"); } catch (e) { /* private mode */ }

// One-time cleanup of deprecated localStorage keys from older versions.
try {
  if (localStorage.getItem("_ft_swipe_hint_seen")) {
    localStorage.removeItem("_ft_swipe_hint_seen");
  }
} catch (e) { /* private mode */ }

// Axios interceptor: attach Bearer token as fallback when cookies might not be sent
axios.interceptors.request.use((config) => {
  if (_bearerToken && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${_bearerToken}`;
  }
  return config;
});

const _saveToken = (token) => {
  _bearerToken = token;
  try { localStorage.setItem("_ft", token); } catch (e) { /* private mode */ }
  try { sessionStorage.setItem("_ft", token); } catch (e) { /* private mode */ }
};

const _clearToken = () => {
  _bearerToken = null;
  try { localStorage.removeItem("_ft"); } catch (e) { /* private mode */ }
  try { sessionStorage.removeItem("_ft"); } catch (e) { /* private mode */ }
};

// Global axios response interceptor: handle timeout errors gracefully
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      error.isTimeout = true;
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  // Hydrate user from localStorage cache for instant first render (avoids loading flash when returning to app).
  const _cachedUser = (() => {
    try {
      const raw = localStorage.getItem("_ft_user");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Accept cache up to 10 minutes old
      if (parsed && parsed._ts && (Date.now() - parsed._ts < 10 * 60 * 1000)) {
        const { _ts, ...userData } = parsed;
        return userData;
      }
    } catch (e) { /* corrupted cache, ignore */ }
    return null;
  })();

  const [user, setUser] = useState(_cachedUser);
  const [loading, setLoading] = useState(!_cachedUser);

  const _cacheUser = (u) => {
    try {
      if (u) localStorage.setItem("_ft_user", JSON.stringify({ ...u, _ts: Date.now() }));
      else localStorage.removeItem("_ft_user");
    } catch (e) { /* private mode */ }
  };

  const checkAuth = useCallback(async () => {
    // Fast path: if no token saved, skip network call
    if (!_bearerToken) {
      _cacheUser(null);
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        timeout: 8000
      });
      setUser(response.data);
      _cacheUser(response.data);
    } catch {
      _clearToken();
      _cacheUser(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData) => {
    if (userData?.token) {
      _saveToken(userData.token);
    }
    setUser(userData);
    _cacheUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    _clearToken();
    _cacheUser(null);
    setUser(null);
  }, []);

  const authValue = useMemo(() => ({ user, loading, login, logout, checkAuth }), [user, loading, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [showTimeout, setShowTimeout] = useState(false);

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowTimeout(true), 5000);
      return () => clearTimeout(timer);
    }
    setShowTimeout(false);
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#002FA7] font-medium text-lg">FUTURA</p>
          {showTimeout && <p className="text-gray-400 text-sm">Conectando...</p>}
        </div>
      </div>
    );
  }

  // With httpOnly cookies, we can't check for token client-side
  // If loading is done and no user, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Handle Android hardware back button via browser history
  useEffect(() => {
    const handlePopState = () => {
      // React Router handles this automatically via BrowserRouter
      // This ensures the WebView doesn't exit on back press
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={detectWebView() ? <Navigate to="/login" replace /> : <Pricing />} />
      <Route path="/checkout" element={detectWebView() ? <Navigate to="/login" replace /> : <Navigate to="/pricing" replace />} />
      <Route path="/subscribe" element={detectWebView() ? <Navigate to="/login" replace /> : <Navigate to="/pricing" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ConsultaDirecta />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consulta"
        element={
          <ProtectedRoute>
            <ConsultaDirecta />
          </ProtectedRoute>
        }
      />
      <Route
        path="/old-dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-decision"
        element={
          <ProtectedRoute>
            <NewDecision />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation/:decisionId"
        element={
          <ProtectedRoute>
            <SimulationChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/result/:decisionId"
        element={
          <ProtectedRoute>
            <SimulationResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history/:id"
        element={
          <ProtectedRoute>
            <ConsultaDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/terminos" element={<Terminos />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/eliminar-cuenta" element={<DeleteAccountPublic />} />
      <Route
        path="/comparador"
        element={
          <ProtectedRoute>
            <Comparador />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bienestar"
        element={
          <ProtectedRoute>
            <MoodTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/biblioteca"
        element={
          <ProtectedRoute>
            <PremiumLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diario"
        element={
          <ProtectedRoute>
            <DiaryAI />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeline"
        element={
          <ProtectedRoute>
            <TimelinePage />
          </ProtectedRoute>
        }
      />
    </Routes>
    <CookieBanner />
    <AvatarWidget />
    </>
  );
}

import { WebViewProvider, detectWebView } from "./hooks/useIsWebView";

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <WebViewProvider>
          <BrowserRouter>
            <AuthProvider>
              <AvatarProvider>
                <AppRouter />
              </AvatarProvider>
            </AuthProvider>
          </BrowserRouter>
        </WebViewProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
